import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex justify-center" }
const _hoisted_2 = {
  class: "text-xs font-normal text-gray-500",
  style: {"max-width":"400px"}
}
const _hoisted_3 = { class: "flex justify-between gap-4" }
const _hoisted_4 = { class: "flex justify-between gap-4" }
const _hoisted_5 = { class: "flex items-center gap-3 mt-1" }
const _hoisted_6 = { class: "block text-sm font-medium leading-6 text-gray-900" }
const _hoisted_7 = { class: "-mt-4 grid grid-cols-2 gap-2" }
const _hoisted_8 = { class: "flex items-center justify-center pb-1" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "-ml-1 mr-2" }

import { PlusIcon } from "@heroicons/vue/24/solid";
import { format } from "date-fns";
import { FieldArray, FieldEntry, Form, SubmissionHandler, ErrorMessage } from "vee-validate";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import LoadingSpinner from "shared/components/loading_state/LoadingSpinner.vue";
import Modal from "shared/components/modals/Modal.vue";
import { useCustomToast } from "shared/composables/toast";
import { countries } from "shared/services/countries";
import { ProjectGoalTypes, ProjectRequest } from "shared/types/Project";
import CheckboxField from "@/components/forms/CheckboxField.vue";
import CurrencyField from "@/components/forms/CurrencyField.vue";
import DateField from "@/components/forms/DateField.vue";
import InputField from "@/components/forms/InputField.vue";
import IntegerField from "@/components/forms/IntegerField.vue";
import SearchListField from "@/components/forms/SearchListField.vue";
import ProjectRepository from "@/repositories/ProjectRepository";
import FileInputAddProjectField from "@/views/dashboard/components/FileInputAddProjectField.vue";

type GoalEntry = {
  type: ProjectGoalTypes;
  enabled: boolean;
};

type FileEntry = {
  size: number;
  name: string;
  file_base64: string;
};

type FormValues = {
  name: string;
  country_code: string;
  zip_code: string;
  street: string;
  estimated_start: string;
  estimated_duration: number;
  estimated_shell_volume: number;
  estimated_cameras: number;
  goals: GoalEntry[];
  files: FileEntry[];
};

const defaultSelectedCountryCode = "DE";
const fileSizeThresholdBytes = 49000000; // 50 MB


export default /*#__PURE__*/_defineComponent({
  __name: 'AddProjectModal',
  props: {
  open: {
    type: Boolean,
    required: true,
  },
},
  emits: ["close"],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();
const emit = __emit;



const countryOptions = countries.map((country) => {
  return {
    value: country.code,
    name: `${country.name} (${country.code})`,
  };
});

const schema = yup.object().shape({
  name: yup.string().required(t("authentication.validation.validation_field_required")),
  country_code: yup.string().required(t("authentication.validation.validation_field_required")),
  street: yup.string().required(t("authentication.validation.validation_field_required")),
  zip_code: yup.string().required(t("authentication.validation.validation_field_required")),
  estimated_duration: yup
    .number()
    .required(t("authentication.validation.validation_field_required")),
  estimated_cameras: yup
    .number()
    .required(t("authentication.validation.validation_field_required")),
  estimated_shell_volume: yup
    .number()
    .required(t("authentication.validation.validation_field_required")),
  estimated_start: yup.date().required(t("authentication.validation.validation_field_required")),
  goals: yup
    .array()
    .of(
      yup.object().shape({
        enabled: yup.boolean().required(),
      }),
    )
    .test(
      "at-least-one-enabled",
      t("project.add.validation.at_least_one_enabled"),
      function (goals) {
        return goals?.some((goal) => goal.enabled === true);
      },
    ),
  files: yup
    .array()
    .test("aggregated-file-size", t("project.add.validation.file_size_exceeded"), function (files) {
      return files?.reduce((sum, obj) => sum + obj.size, 0) < fileSizeThresholdBytes;
    }),
});

const initializeForm = () => {
  return {
    country_code: defaultSelectedCountryCode,
    goals: [
      { type: "cameras", enabled: false },
      { type: "shorten_construction_time", enabled: false },
      { type: "target_actual_schedule", enabled: false },
      { type: "unit_values", enabled: false },
      { type: "claims", enabled: false },
      { type: "site_management_assistant", enabled: false },
    ],
    files: [],
  };
};

const handleSubmit: SubmissionHandler = async (genericObject) => {
  const values = genericObject as FormValues;

  const payload = {
    ...values,
    estimated_start: format(values.estimated_start, "yyyy-MM-dd"),
    goals: values.goals.filter((item) => item.enabled).map((item) => item.type),
    files: values.files.map((item) => {
      return {
        name: item.name,
        file_base64: item.file_base64,
      };
    }),
  } as ProjectRequest;

  return ProjectRepository.requestProject(payload).then((data) => {
    if (data.status === "OK") {
      useCustomToast().success(t("project.add.success_message"));
      emit("close");
    }
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    open: __props.open,
    onClose: _cache[0] || (_cache[0] = ($event: any) => (emit('close'))),
    customCls: "w-full md:w-3/4 xl:w-1/2"
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_ctx.$t(`project.add.title`)) + " ", 1),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, _toDisplayString(_ctx.$t(`project.add.subtitle`)), 1)
      ])
    ]),
    content: _withCtx(() => [
      _createVNode(_unref(Form), {
        onSubmit: handleSubmit,
        validationSchema: _unref(schema),
        initialValues: initializeForm(),
        class: "flex flex-col gap-4 px-4 overflow-auto"
      }, {
        default: _withCtx(({ submitCount, isSubmitting }) => [
          _createVNode(InputField, {
            name: "name",
            minWidth: 50,
            label: _ctx.$t(`project.add.name`)
          }, null, 8, ["label"]),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(SearchListField, {
              class: "w-full",
              name: "country_code",
              label: _ctx.$t(`project.add.country_code`),
              options: _unref(countryOptions)
            }, null, 8, ["label", "options"]),
            _createVNode(InputField, {
              class: "w-full",
              minWidth: 50,
              name: "street",
              label: _ctx.$t(`project.add.street`)
            }, null, 8, ["label"]),
            _createVNode(InputField, {
              class: "w-full",
              minWidth: 50,
              name: "zip_code",
              label: _ctx.$t(`project.add.zip_code`)
            }, null, 8, ["label"])
          ]),
          _createVNode(DateField, {
            name: "estimated_start",
            label: _ctx.$t(`project.add.estimated_start`)
          }, null, 8, ["label"]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(IntegerField, {
              class: "w-full",
              name: "estimated_duration",
              label: _ctx.$t(`project.add.estimated_duration`)
            }, null, 8, ["label"]),
            _createVNode(CurrencyField, {
              class: "w-full",
              name: "estimated_shell_volume",
              label: _ctx.$t(`project.add.estimated_shell_volume`)
            }, null, 8, ["label"]),
            _createVNode(IntegerField, {
              class: "w-full",
              name: "estimated_cameras",
              label: _ctx.$t(`project.add.estimated_cameras`)
            }, null, 8, ["label"])
          ]),
          _createVNode(_unref(FieldArray), { name: "goals" }, {
            default: _withCtx(({ fields }) => [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.$t(`project.add.project_goals`)), 1),
                (submitCount > 0)
                  ? (_openBlock(), _createBlock(_unref(ErrorMessage), {
                      key: 0,
                      class: "text-xs text-red",
                      name: "goals"
                    }))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_7, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fields as FieldEntry<GoalEntry>[], (field, idx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: idx,
                    class: "inline-block max-w-fit"
                  }, [
                    _createVNode(CheckboxField, {
                      name: `goals[${idx}].enabled`,
                      label: _ctx.$t(`project.add.goals.${field.value.type}`),
                      labelFontWeight: 400
                    }, null, 8, ["name", "label"])
                  ]))
                }), 128))
              ])
            ]),
            _: 2
          }, 1024),
          _createVNode(FileInputAddProjectField, { name: "files" }),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("button", {
              disabled: isSubmitting,
              type: "submit",
              class: "focus:outline-none inline-flex justify-center rounded-md border border-transparent bg-yellow-600 px-5 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
            }, [
              _createElementVNode("div", _hoisted_10, [
                (!isSubmitting)
                  ? (_openBlock(), _createBlock(_unref(PlusIcon), {
                      key: 0,
                      class: "h-5 w-5",
                      "aria-hidden": "true"
                    }))
                  : (_openBlock(), _createBlock(LoadingSpinner, {
                      key: 1,
                      size: "w-5 h-5",
                      color: "white"
                    }))
              ]),
              _createTextVNode(" " + _toDisplayString(_ctx.$t(`project.add.add_project`)), 1)
            ], 8, _hoisted_9)
          ])
        ]),
        _: 1
      }, 8, ["validationSchema", "initialValues"])
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})